import { useDeferredValue, useEffect, useMemo, useState } from "react"

import { FleetB2BPageShell } from "@/features/operator/fleet/fleet-b2b-page-shell"
import {
  formatFleetMasterTimestamp,
  useFleetMasterDataWorkspaceContext,
} from "@/features/operator/fleet/fleet-master-data-shared"
import type { FleetOnboardingSearch } from "@/features/operator/fleet/fleet-onboarding-shared"
import {
  OperatorEmptyState,
  OperatorMetricCard,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  apiClient,
  type FleetAssetRecord,
  type FleetAssignmentRecord,
  type FleetPersonRecord,
  isApiError,
} from "@/lib/api/client"

interface FleetAssignmentsPageProps {
  search?: FleetOnboardingSearch
}

interface FleetAssignmentFormState {
  assetId: string
  assignmentStatus: string
  primaryPersonId: string
  secondaryPersonId: string
}

const DEFAULT_FORM_STATE: FleetAssignmentFormState = {
  assetId: "",
  assignmentStatus: "assigned",
  primaryPersonId: "",
  secondaryPersonId: "",
}

function toFormState(record?: FleetAssignmentRecord | null): FleetAssignmentFormState {
  if (!record) {
    return DEFAULT_FORM_STATE
  }

  return {
    assetId: record.assetId,
    assignmentStatus: record.assignmentStatus,
    primaryPersonId: record.primaryPersonId || "",
    secondaryPersonId: record.secondaryPersonId || "",
  }
}

export function FleetAssignmentsPage({ search }: FleetAssignmentsPageProps) {
  const { organizationId, organizationLabel, sessionError, sessionStatus, vertical } =
    useFleetMasterDataWorkspaceContext(search)
  const [assignments, setAssignments] = useState<FleetAssignmentRecord[]>([])
  const [assets, setAssets] = useState<FleetAssetRecord[]>([])
  const [people, setPeople] = useState<FleetPersonRecord[]>([])
  const [formState, setFormState] = useState<FleetAssignmentFormState>(DEFAULT_FORM_STATE)
  const [editingAssignmentId, setEditingAssignmentId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeletingAssignmentId, setIsDeletingAssignmentId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [searchText, setSearchText] = useState("")
  const deferredSearchText = useDeferredValue(searchText)

  useEffect(() => {
    if (!organizationId) {
      setAssignments([])
      setAssets([])
      setPeople([])
      return
    }

    let isActive = true
    setIsLoading(true)
    setError(null)

    void Promise.all([
      apiClient.getFleetAssignments({ organizationId, vertical }),
      apiClient.getFleetAssets({ organizationId, vertical }),
      apiClient.getFleetPeople({ organizationId, vertical }),
    ])
      .then(([assignmentRecords, assetRecords, peopleRecords]) => {
        if (!isActive) {
          return
        }

        setAssignments(assignmentRecords)
        setAssets(assetRecords)
        setPeople(peopleRecords)
      })
      .catch((loadError) => {
        if (!isActive) {
          return
        }

        setError(
          isApiError(loadError)
            ? loadError.message
            : loadError instanceof Error
              ? loadError.message
              : "Failed to load fleet assignments."
        )
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [organizationId, vertical])

  const filteredAssignments = useMemo(() => {
    const query = deferredSearchText.trim().toLowerCase()
    if (!query) {
      return assignments
    }

    return assignments.filter((assignment) =>
      [
        assignment.asset?.assetNumber || "",
        assignment.asset?.operatingUnit?.code || "",
        assignment.primaryPerson?.fullName || "",
        assignment.secondaryPerson?.fullName || "",
        assignment.assignmentStatus,
      ].some((value) => value.toLowerCase().includes(query))
    )
  }, [assignments, deferredSearchText])

  const summary = useMemo(() => {
    return {
      activeAssignments: assignments.filter((assignment) => assignment.assignmentStatus === "assigned").length,
      assetsWithoutAssignments: Math.max(assets.length - assignments.length, 0),
      uniquePrimaryPeople: new Set(assignments.map((assignment) => assignment.primaryPersonId).filter(Boolean)).size,
      uniqueSecondaryPeople: new Set(assignments.map((assignment) => assignment.secondaryPersonId).filter(Boolean)).size,
    }
  }, [assets.length, assignments])

  const availableAssets = useMemo(() => {
    if (editingAssignmentId) {
      return assets
    }

    const assignedAssetIds = new Set(assignments.map((assignment) => assignment.assetId))
    return assets.filter((asset) => !assignedAssetIds.has(asset.id))
  }, [assets, assignments, editingAssignmentId])

  function resetForm() {
    setEditingAssignmentId(null)
    setFormState(DEFAULT_FORM_STATE)
  }

  function beginEditing(assignment: FleetAssignmentRecord) {
    setEditingAssignmentId(assignment.id)
    setFormState(toFormState(assignment))
    setNotice(null)
    setError(null)
  }

  async function saveAssignment() {
    if (!organizationId) {
      setError("No Fleet organization is selected for assignment management.")
      return
    }

    setIsSaving(true)
    setNotice(null)
    setError(null)

    try {
      const payload = {
        ...formState,
        organizationId,
        primaryPersonId: formState.primaryPersonId || null,
        secondaryPersonId: formState.secondaryPersonId || null,
        vertical,
      }

      const savedAssignment = editingAssignmentId
        ? await apiClient.updateFleetAssignment(editingAssignmentId, payload)
        : await apiClient.createFleetAssignment(payload)

      setAssignments((previous) =>
        editingAssignmentId
          ? previous.map((assignment) =>
              assignment.id === savedAssignment.id ? savedAssignment : assignment
            )
          : [savedAssignment, ...previous]
      )
      setNotice(
        editingAssignmentId
          ? `Updated assignment for ${savedAssignment.asset?.assetNumber || "asset"}.`
          : `Created assignment for ${savedAssignment.asset?.assetNumber || "asset"}.`
      )
      resetForm()
    } catch (saveError) {
      setError(
        isApiError(saveError)
          ? saveError.message
          : saveError instanceof Error
            ? saveError.message
            : "Failed to save the fleet assignment."
      )
    } finally {
      setIsSaving(false)
    }
  }

  async function deleteAssignment(assignment: FleetAssignmentRecord) {
    if (!organizationId) {
      return
    }

    setIsDeletingAssignmentId(assignment.id)
    setNotice(null)
    setError(null)

    try {
      await apiClient.deleteFleetAssignment(assignment.id, organizationId)
      setAssignments((previous) => previous.filter((record) => record.id !== assignment.id))
      if (editingAssignmentId === assignment.id) {
        resetForm()
      }
      setNotice(`Deleted assignment for ${assignment.asset?.assetNumber || "asset"}.`)
    } catch (deleteError) {
      setError(
        isApiError(deleteError)
          ? deleteError.message
          : deleteError instanceof Error
            ? deleteError.message
            : "Failed to delete the fleet assignment."
      )
    } finally {
      setIsDeletingAssignmentId(null)
    }
  }

  return (
    <FleetB2BPageShell
      activeSection="assignments"
      title="Asset assignments"
      description="Track which people are attached to which asset and preserve assignment history across driver, helper, conductor, and rider changes."
      highlights={[
        "Assignments are explicit persisted records instead of flattened onboarding rows.",
        "Each asset can now be paired with primary and secondary people independently.",
        "Readiness, issuance, and analytics now read from the same persisted assignment graph.",
      ]}
    >
      <div className="grid gap-5">
        <OperatorStatusMessage>
          Active Fleet organization: <strong>{organizationLabel}</strong>.
        </OperatorStatusMessage>

        {sessionStatus === "loading" ? (
          <OperatorStatusMessage>Loading Fleet organization context…</OperatorStatusMessage>
        ) : null}
        {sessionError ? <OperatorStatusMessage tone="danger">{sessionError}</OperatorStatusMessage> : null}
        {error ? <OperatorStatusMessage tone="danger">{error}</OperatorStatusMessage> : null}
        {notice ? <OperatorStatusMessage>{notice}</OperatorStatusMessage> : null}

        {!organizationId ? (
          <OperatorPanel
            title="Organization context required"
            description="This workspace needs an organization scope from the current operator session or the latest Fleet onboarding persistence."
          >
            <OperatorEmptyState
              title="No Fleet organization is currently selected."
              message="Finish a Fleet onboarding review or open this workspace from an organization-bound operator session to manage persisted assignments."
            />
          </OperatorPanel>
        ) : (
          <>
            <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <OperatorMetricCard
                label="Assignments"
                value={assignments.length}
                detail="Persisted asset-to-person assignment records."
              />
              <OperatorMetricCard
                label="Assigned now"
                value={summary.activeAssignments}
                detail="Assignments currently in the assigned state."
              />
              <OperatorMetricCard
                label="Unassigned assets"
                value={summary.assetsWithoutAssignments}
                detail="Persisted assets that still need assignment coverage."
              />
              <OperatorMetricCard
                label="Crew coverage"
                value={`${summary.uniquePrimaryPeople}/${summary.uniqueSecondaryPeople}`}
                detail="Primary and secondary people currently referenced by assignments."
              />
            </section>

            <section className="grid gap-5 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
              <OperatorPanel
                title={editingAssignmentId ? "Edit assignment" : "Create assignment"}
                description="Create or update the persisted asset-to-person link that powers issuance and readiness."
              >
                <div className="grid gap-4">
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Asset</span>
                    <select
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.assetId}
                      onChange={(event) =>
                        setFormState((previous) => ({ ...previous, assetId: event.target.value }))
                      }
                    >
                      <option value="">Select an asset</option>
                      {availableAssets.map((asset) => (
                        <option key={asset.id} value={asset.id}>
                          {asset.assetNumber} • {asset.operatingUnit?.code || "No unit"}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Primary person</span>
                    <select
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.primaryPersonId}
                      onChange={(event) =>
                        setFormState((previous) => ({
                          ...previous,
                          primaryPersonId: event.target.value,
                        }))
                      }
                    >
                      <option value="">Unassigned primary person</option>
                      {people.map((person) => (
                        <option key={person.id} value={person.id}>
                          {person.fullName} • {person.roleType}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Secondary person</span>
                    <select
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.secondaryPersonId}
                      onChange={(event) =>
                        setFormState((previous) => ({
                          ...previous,
                          secondaryPersonId: event.target.value,
                        }))
                      }
                    >
                      <option value="">Unassigned secondary person</option>
                      {people.map((person) => (
                        <option key={person.id} value={person.id}>
                          {person.fullName} • {person.roleType}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="grid gap-2 text-sm">
                    <span className="text-muted-foreground">Assignment status</span>
                    <select
                      className="rounded-2xl border border-border bg-background px-3 py-2"
                      value={formState.assignmentStatus}
                      onChange={(event) =>
                        setFormState((previous) => ({
                          ...previous,
                          assignmentStatus: event.target.value,
                        }))
                      }
                    >
                      <option value="assigned">Assigned</option>
                      <option value="temporary">Temporary</option>
                      <option value="reserve">Reserve</option>
                      <option value="ended">Ended</option>
                    </select>
                  </label>
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => void saveAssignment()}
                    disabled={isSaving}
                    className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
                  >
                    {isSaving ? "Saving…" : editingAssignmentId ? "Save assignment" : "Create assignment"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
                  >
                    Clear form
                  </button>
                </div>
              </OperatorPanel>

              <OperatorPanel
                title="Persisted assignment graph"
                description="Review current asset coverage, search by asset or person, and update the active crew link here."
              >
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <label className="min-w-[18rem] flex-1 text-sm">
                    <span className="mb-2 block text-muted-foreground">Search assignments</span>
                    <input
                      className="w-full rounded-2xl border border-border bg-background px-3 py-2"
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                      placeholder="Search by asset, unit, person, or status"
                    />
                  </label>
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-3 text-sm text-muted-foreground">
                    Available assets for new assignment: <strong>{availableAssets.length}</strong>
                  </div>
                </div>

                {isLoading ? (
                  <div className="text-sm text-muted-foreground">Loading persisted assignments…</div>
                ) : filteredAssignments.length === 0 ? (
                  <OperatorEmptyState
                    title="No assignments match the current view."
                    message="Create the first persisted assignment or broaden the search to see existing records."
                  />
                ) : (
                  <div className="grid gap-3">
                    {filteredAssignments.map((assignment) => (
                      <div
                        key={assignment.id}
                        className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="text-lg font-semibold">
                              {assignment.asset?.assetNumber || "Unknown asset"}
                            </div>
                            <div className="mt-1 text-sm text-muted-foreground">
                              {assignment.asset?.operatingUnit?.code || "No unit"} • {assignment.assignmentStatus}
                            </div>
                            <div className="mt-1 text-xs uppercase tracking-[0.16em] text-muted-foreground">
                              {assignment.asset?.assetType || "asset"}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => beginEditing(assignment)}
                              className="rounded-2xl border border-border px-3 py-2 text-sm font-medium"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => void deleteAssignment(assignment)}
                              disabled={isDeletingAssignmentId === assignment.id}
                              className="rounded-2xl border border-destructive/40 px-3 py-2 text-sm font-medium text-destructive disabled:opacity-60"
                            >
                              {isDeletingAssignmentId === assignment.id ? "Deleting…" : "Delete"}
                            </button>
                          </div>
                        </div>

                        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                          <OperatorMetricCard
                            label="Primary person"
                            value={assignment.primaryPerson?.fullName || "Unassigned"}
                            detail={assignment.primaryPerson?.roleType || "No primary person linked yet."}
                          />
                          <OperatorMetricCard
                            label="Secondary person"
                            value={assignment.secondaryPerson?.fullName || "Unassigned"}
                            detail={assignment.secondaryPerson?.roleType || "No secondary person linked yet."}
                          />
                          <OperatorMetricCard
                            label="Route / zone"
                            value={assignment.asset?.routeOrZone || "Not set"}
                            detail={assignment.asset?.status || "Asset status unavailable."}
                          />
                          <OperatorMetricCard
                            label="Updated"
                            value={formatFleetMasterTimestamp(assignment.updatedAt)}
                            detail={`Created ${formatFleetMasterTimestamp(assignment.createdAt)}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </OperatorPanel>
            </section>
          </>
        )}
      </div>
    </FleetB2BPageShell>
  )
}
